import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { RootLayout } from "@shared/layouts/RootLayout";
import { apiResponseErrorHandler } from "@shared/apis/sidekick/api-error-handler";
import { useAdminImpersonateCodeExchange } from "@queries/auth/useAdminImpersonateCodeExchange";

// This module is imported statically (not lazily) all the way from app.tsx,
// so this runs before app.tsx's own body, including the analytics SDK
// init() calls below it - the code and code_verifier are out of the URL
// before any pageview can capture them.
const isCallbackUrl =
  window.location.pathname === "/admin-impersonate/callback";
const code = isCallbackUrl
  ? new URLSearchParams(window.location.search).get("code")
  : null;
const codeVerifier = isCallbackUrl
  ? new URLSearchParams(window.location.hash.replace(/^#/, "")).get(
      "code_verifier",
    )
  : null;
if (isCallbackUrl) {
  window.history.replaceState(null, "", window.location.pathname);
}

export const AdminImpersonateCallbackPage = () => {
  const [error, setError] = useState<string | null>(null);
  const hasExchangedRef = useRef(false);
  const { mutate: exchangeCode } = useAdminImpersonateCodeExchange();

  useEffect(() => {
    // Guards against React StrictMode's dev-only double-invoke: the code is
    // single-use, so firing this twice would exchange it once and then fail
    // the second time with a misleading "invalid or expired code" error.
    if (hasExchangedRef.current) return;
    hasExchangedRef.current = true;

    if (!code || !codeVerifier) {
      setError("This impersonation link is invalid.");
      return;
    }

    exchangeCode(
      { code, codeVerifier },
      {
        onSuccess: () => window.location.replace("/"),
        onError: async (err) => {
          const apiError = await apiResponseErrorHandler(err);
          setError(
            apiError.errors[0] ??
              "This impersonation link has expired or already been used.",
          );
        },
      },
    );
  }, [exchangeCode]);

  return (
    <RootLayout theme="light">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        gap={2}
        textAlign="center"
        px={2}
      >
        <Typography variant="h6">
          {error ? "Something went wrong" : "Signing you in…"}
        </Typography>
        {error && (
          <>
            <Typography color="secondary">{error}</Typography>
            <SubmitButton onClick={() => (window.location.href = "/login")}>
              Back to sign in
            </SubmitButton>
          </>
        )}
      </Box>
    </RootLayout>
  );
};
